import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, CaretRight, MagnifyingGlass, Globe, Wine, Leaf } from '@phosphor-icons/react';

const RegionsOverviewPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Global wine region categories that span multiple countries
  const regionCategories = [
    {
      id: 'mediterranean',
      name: 'Régions Méditerranéennes',
      description: 'Climat chaud et sec, vins puissants et fruités',
      icon: <Globe size={24} color="#4B164C" weight="light" />,
      regions: [
        { name: 'Provence', country: 'France', type: 'Rouge/Rosé', climate: 'Méditerranéen' },
        { name: 'Toscane', country: 'Italie', type: 'Rouge', climate: 'Méditerranéen' },
        { name: 'Rioja', country: 'Espagne', type: 'Rouge', climate: 'Méditerranéen' },
        { name: 'Douro', country: 'Portugal', type: 'Rouge/Fortifié', climate: 'Méditerranéen' },
        { name: 'Sicile', country: 'Italie', type: 'Rouge/Blanc', climate: 'Méditerranéen' }
      ]
    },
    {
      id: 'continental',
      name: 'Régions Continentales',
      description: 'Climat tempéré, vins élégants et structurés',
      icon: <MapPin size={24} color="#4B164C" weight="light" />,
      regions: [
        { name: 'Bordeaux', country: 'France', type: 'Rouge/Blanc', climate: 'Océanique' },
        { name: 'Bourgogne', country: 'France', type: 'Rouge/Blanc', climate: 'Continental' },
        { name: 'Piémont', country: 'Italie', type: 'Rouge', climate: 'Continental' },
        { name: 'Ribera del Duero', country: 'Espagne', type: 'Rouge', climate: 'Continental' },
        { name: 'Dão', country: 'Portugal', type: 'Rouge/Blanc', climate: 'Continental' }
      ]
    },
    {
      id: 'mountain',
      name: 'Régions de Montagne',
      description: 'Altitude élevée, vins frais et aromatiques',
      icon: <MapPin size={24} color="#4B164C" weight="light" />,
      regions: [
        { name: 'Jura', country: 'France', type: 'Rouge/Blanc/Effervescent', climate: 'Montagneux' },
        { name: 'Valais', country: 'Suisse', type: 'Rouge/Blanc', climate: 'Alpin' },
        { name: 'Mendoza', country: 'Argentine', type: 'Rouge/Blanc', climate: 'Andin' },
        { name: 'Vallée de la Loire', country: 'France', type: 'Blanc/Rouge', climate: 'Océanique' }
      ]
    },
    {
      id: 'coastal',
      name: 'Régions Côtières',
      description: 'Influence maritime, vins frais et minéraux',
      icon: <Globe size={24} color="#4B164C" weight="light" />,
      regions: [
        { name: 'Alsace', country: 'France', type: 'Blanc', climate: 'Continental' },
        { name: 'Vénétie', country: 'Italie', type: 'Blanc/Rouge', climate: 'Méditerranéen' },
        { name: 'Vinho Verde', country: 'Portugal', type: 'Blanc/Rouge', climate: 'Atlantique' },
        { name: 'Casablanca', country: 'Chili', type: 'Blanc/Rouge', climate: 'Pacifique' },
        { name: 'Margaret River', country: 'Australie', type: 'Rouge/Blanc', climate: 'Océanique' }
      ]
    },
    {
      id: 'new-world',
      name: 'Nouveau Monde',
      description: 'Innovation et cépages internationaux',
      icon: <Wine size={24} color="#4B164C" weight="light" />,
      regions: [
        { name: 'Barossa Valley', country: 'Australie', type: 'Rouge', climate: 'Méditerranéen' },
        { name: 'Maipo', country: 'Chili', type: 'Rouge', climate: 'Méditerranéen' },
        { name: 'Baja California', country: 'Mexique', type: 'Rouge/Blanc', climate: 'Méditerranéen' },
        { name: 'Hunter Valley', country: 'Australie', type: 'Rouge/Blanc', climate: 'Subtropical' }
      ]
    }
  ];

  // Climate and terroir characteristics
  const climateCharacteristics = [
    {
      name: 'Méditerranéen',
      description: 'Étés chauds et secs, hivers doux',
      characteristics: ['Vins puissants', 'Maturité optimale', 'Tanins mûrs'],
      regions: ['Provence', 'Toscane', 'Rioja', 'Douro']
    },
    {
      name: 'Continental',
      description: 'Étés chauds, hivers froids',
      characteristics: ['Vins structurés', 'Acidité vive', 'Aging potential'],
      regions: ['Bordeaux', 'Bourgogne', 'Piémont', 'Ribera del Duero']
    },
    {
      name: 'Océanique',
      description: 'Influence maritime modératrice',
      characteristics: ['Vins équilibrés', 'Fraîcheur', 'Minéralité'],
      regions: ['Bordeaux', 'Margaret River', 'Casablanca']
    },
    {
      name: 'Montagneux',
      description: 'Altitude élevée, amplitudes thermiques',
      characteristics: ['Vins aromatiques', 'Acidité vive', 'Complexité'],
      regions: ['Jura', 'Valais', 'Mendoza']
    }
  ];

  // Wine style comparisons across regions
  const styleComparisons = [
    {
      style: 'Vins Rouges Puissants',
      regions: [
        { name: 'Bordeaux', country: 'France', grape: 'Cabernet Sauvignon', style: 'Structuré et complexe' },
        { name: 'Rioja', country: 'Espagne', grape: 'Tempranillo', style: 'Élégant et fruité' },
        { name: 'Barossa Valley', country: 'Australie', grape: 'Shiraz', style: 'Concentré et épicé' },
        { name: 'Maipo', country: 'Chili', grape: 'Cabernet Sauvignon', style: 'Fruité et accessible' }
      ]
    },
    {
      style: 'Vins Blancs Élégants',
      regions: [
        { name: 'Bourgogne', country: 'France', grape: 'Chardonnay', style: 'Minéral et complexe' },
        { name: 'Alsace', country: 'France', grape: 'Riesling', style: 'Aromatique et sec' },
        { name: 'Margaret River', country: 'Australie', grape: 'Chardonnay', style: 'Riche et tropical' },
        { name: 'Casablanca', country: 'Chili', grape: 'Sauvignon Blanc', style: 'Vif et herbacé' }
      ]
    }
  ];

  // Filter categories based on selection
  const filteredCategories = regionCategories.filter(category => 
    selectedCategory === 'all' || category.id === selectedCategory
  );

  // Filter regions within categories based on search term
  const getFilteredRegions = () => {
    return filteredCategories.map(category => ({
      ...category,
      regions: category.regions.filter(region =>
        region.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        region.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
        region.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        region.climate.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })).filter(category => category.regions.length > 0);
  };

  const filteredCategoriesWithRegions = getFilteredRegions();

  // Get all regions for search results display
  const allRegions = regionCategories.flatMap(category => 
    category.regions.filter(region =>
      region.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      region.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
      region.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      region.climate.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1 style={{ 
          fontSize: '2.5rem', 
          color: '#4B164C', 
          marginBottom: '10px',
          fontWeight: '700'
        }}>
          Régions Viticoles du Monde
        </h1>
        <p style={{ 
          fontSize: '1.2rem', 
          color: '#666', 
          maxWidth: '700px', 
          margin: '0 auto',
          lineHeight: '1.6'
        }}>
          Découvrez les régions viticoles par climat, style et caractéristiques communes
        </p>
      </div>

      {/* Search and Filter */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: '15px', 
        marginBottom: '30px',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{ position: 'relative' }}>
          <MagnifyingGlass 
            size={20} 
            color="#666" 
            style={{ 
              position: 'absolute', 
              left: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)' 
            }} 
          />
          <input
            type="text"
            placeholder="Rechercher une région, pays ou type de vin..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '12px 12px 12px 40px',
              border: '2px solid #e8e0e8',
              borderRadius: '8px',
              fontSize: '1rem',
              width: '300px',
              outline: 'none'
            }}
            onFocus={e => e.target.style.borderColor = '#4B164C'}
            onBlur={e => e.target.style.borderColor = '#e8e0e8'}
          />
        </div>
        
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{
            padding: '12px',
            border: '2px solid #e8e0e8',
            borderRadius: '8px',
            fontSize: '1rem',
            background: 'white',
            outline: 'none'
          }}
        >
          <option value="all">Toutes les catégories</option>
          {regionCategories.map(category => (
            <option key={category.id} value={category.id}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      {/* Search Results Summary */}
      {searchTerm && (
        <div style={{ 
          marginBottom: '30px', 
          padding: '15px', 
          background: '#f0e6f0', 
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <p style={{ 
            color: '#4B164C', 
            fontSize: '1rem',
            margin: '0',
            fontWeight: '500'
          }}>
            {allRegions.length} région{allRegions.length > 1 ? 's' : ''} trouvée{allRegions.length > 1 ? 's' : ''} pour "{searchTerm}"
          </p>
        </div>
      )}

      {/* Regional Categories - MOVED TO TOP */}
      <div style={{ marginBottom: '50px' }}>
        <h2 style={{ 
          fontSize: '1.8rem', 
          color: '#4B164C', 
          marginBottom: '30px',
          textAlign: 'center'
        }}>
          {searchTerm || selectedCategory !== 'all' ? 'Résultats de Recherche' : 'Catégories Régionales'}
        </h2>
        
        {filteredCategoriesWithRegions.length > 0 ? (
          filteredCategoriesWithRegions.map((category, index) => (
            <div key={index} style={{ marginBottom: '30px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '16px', 
                marginBottom: '20px',
                padding: '20px',
                background: '#f8f6fa',
                borderRadius: '12px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: '50px',
                  height: '50px',
                  background: '#fff',
                  borderRadius: '10px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  {category.icon}
                </div>
                <div>
                  <h3 style={{ 
                    fontSize: '1.4rem', 
                    color: '#4B164C', 
                    margin: '0 0 8px 0',
                    fontWeight: '600'
                  }}>
                    {category.name}
                  </h3>
                  <p style={{ 
                    color: '#666', 
                    fontSize: '1rem',
                    margin: '0',
                    lineHeight: '1.5'
                  }}>
                    {category.description}
                  </p>
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                gap: '15px' 
              }}>
                {category.regions.map((region, regionIndex) => (
                  <div
                    key={regionIndex}
                    style={{
                      background: '#fff',
                      borderRadius: '10px',
                      border: '2px solid #e8e0e8',
                      padding: '18px',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      marginBottom: '12px'
                    }}>
                      <h4 style={{ 
                        fontSize: '1.1rem', 
                        color: '#4B164C', 
                        margin: '0',
                        fontWeight: '600'
                      }}>
                        {region.name}
                      </h4>
                      <span style={{
                        padding: '4px 8px',
                        background: '#f0e6f0',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        color: '#4B164C',
                        fontWeight: '500'
                      }}>
                        {region.type}
                      </span>
                    </div>
                    
                    <p style={{ 
                      color: '#666', 
                      fontSize: '0.9rem',
                      margin: '0 0 8px 0'
                    }}>
                      <strong>Pays:</strong> {region.country}
                    </p>
                    <p style={{ 
                      color: '#666', 
                      fontSize: '0.9rem',
                      margin: '0'
                    }}>
                      <strong>Climat:</strong> {region.climate}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            background: '#f8f6fa', 
            borderRadius: '12px' 
          }}>
            <p style={{ 
              color: '#666', 
              fontSize: '1.1rem',
              margin: '0'
            }}>
              {searchTerm ? `Aucune région trouvée pour "${searchTerm}"` : 'Aucune région disponible dans cette catégorie'}
            </p>
          </div>
        )}
      </div>

      {/* Climate Characteristics */}
      <div style={{ marginBottom: '50px' }}>
        <h2 style={{ 
          fontSize: '1.8rem', 
          color: '#4B164C', 
          marginBottom: '30px',
          textAlign: 'center'
        }}>
          Climats et Terroirs
        </h2>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
          gap: '20px' 
        }}>
          {climateCharacteristics.map((climate, index) => (
            <div
              key={index}
              style={{
                background: '#fff',
                borderRadius: '12px',
                border: '2px solid #e8e0e8',
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <h3 style={{ 
                fontSize: '1.3rem', 
                color: '#4B164C', 
                margin: '0 0 8px 0',
                fontWeight: '600'
              }}>
                {climate.name}
              </h3>
              <p style={{ 
                color: '#666', 
                fontSize: '0.95rem',
                margin: '0 0 16px 0',
                lineHeight: '1.5'
              }}>
                {climate.description}
              </p>
              
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ 
                  fontSize: '1rem', 
                  color: '#4B164C', 
                  margin: '0 0 8px 0',
                  fontWeight: '500'
                }}>
                  Caractéristiques
                </h4>
                <ul style={{ 
                  color: '#666', 
                  fontSize: '0.9rem',
                  margin: '0',
                  paddingLeft: '20px'
                }}>
                  {climate.characteristics.map((char, idx) => (
                    <li key={idx} style={{ marginBottom: '4px' }}>{char}</li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 style={{ 
                  fontSize: '1rem', 
                  color: '#4B164C', 
                  margin: '0 0 8px 0',
                  fontWeight: '500'
                }}>
                  Régions représentatives
                </h4>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '6px' 
                }}>
                  {climate.regions.map((region, idx) => (
                    <span
                      key={idx}
                      style={{
                        padding: '4px 8px',
                        background: '#f0e6f0',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        color: '#4B164C'
                      }}
                    >
                      {region}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Style Comparisons */}
      <div style={{ marginBottom: '50px' }}>
        <h2 style={{ 
          fontSize: '1.8rem', 
          color: '#4B164C', 
          marginBottom: '30px',
          textAlign: 'center'
        }}>
          Comparaisons de Styles
        </h2>
        
        {styleComparisons.map((comparison, index) => (
          <div key={index} style={{ marginBottom: '30px' }}>
            <h3 style={{ 
              fontSize: '1.4rem', 
              color: '#4B164C', 
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              {comparison.style}
            </h3>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
              gap: '20px' 
            }}>
              {comparison.regions.map((region, regionIndex) => (
                <div
                  key={regionIndex}
                  style={{
                    background: '#f8f6fa',
                    borderRadius: '12px',
                    border: '2px solid #e8e0e8',
                    padding: '20px',
                    textAlign: 'center'
                  }}
                >
                  <h4 style={{ 
                    fontSize: '1.1rem', 
                    color: '#4B164C', 
                    margin: '0 0 8px 0',
                    fontWeight: '600'
                  }}>
                    {region.name}
                  </h4>
                  <p style={{ 
                    color: '#666', 
                    fontSize: '0.9rem',
                    margin: '0 0 8px 0'
                  }}>
                    {region.country}
                  </p>
                  <p style={{ 
                    color: '#4B164C', 
                    fontSize: '0.9rem',
                    margin: '0 0 8px 0',
                    fontWeight: '500'
                  }}>
                    Cépage: {region.grape}
                  </p>
                  <p style={{ 
                    color: '#666', 
                    fontSize: '0.85rem',
                    margin: '0',
                    fontStyle: 'italic'
                  }}>
                    {region.style}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Links */}
      <div style={{ 
        padding: '30px', 
        background: '#f8f6fa', 
        borderRadius: '12px',
        textAlign: 'center'
      }}>
        <h3 style={{ 
          fontSize: '1.3rem', 
          color: '#4B164C', 
          marginBottom: '15px',
          fontWeight: '600'
        }}>
          Explorez par Pays
        </h3>
        <p style={{ 
          color: '#666', 
          fontSize: '1rem',
          marginBottom: '20px',
          lineHeight: '1.5'
        }}>
          Découvrez les régions détaillées de chaque pays viticole
        </p>
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '15px', 
          justifyContent: 'center' 
        }}>
          <Link
            to="/info/countries"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: '#4B164C',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '500',
              transition: 'background 0.2s'
            }}
            onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
            onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
          >
            Tous les Pays
            <CaretRight size={16} />
          </Link>
          <Link
            to="/info/styles"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: '#6d2c7a',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '500',
              transition: 'background 0.2s'
            }}
            onMouseOver={e => e.currentTarget.style.background = '#8a4a8a'}
            onMouseOut={e => e.currentTarget.style.background = '#6d2c7a'}
          >
            Styles de Vin
            <CaretRight size={16} />
          </Link>
          <Link
            to="/info"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              background: '#f0e6f0',
              color: '#4B164C',
              textDecoration: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '500',
              border: '2px solid #4B164C',
              transition: 'all 0.2s'
            }}
            onMouseOver={e => {
              e.currentTarget.style.background = '#4B164C';
              e.currentTarget.style.color = 'white';
            }}
            onMouseOut={e => {
              e.currentTarget.style.background = '#f0e6f0';
              e.currentTarget.style.color = '#4B164C';
            }}
          >
            Guide du Vin
            <CaretRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegionsOverviewPage; 